import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Divider,
  Modal,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import MainLayout from "layouts/Main";
import { NORD_POOL_TABS } from "helpers/tabs";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectNordPoolPriceSteps } from "store/selectors";
import {
  fetchNordPoolPriceSteps,
  saveNordPoolPriceSteps,
} from "store/appSlice";
import { forceUpdateNordPoolPriceSteps } from "api/resources/nordPool";
import ConfirmationModal from "components/Modals/Confirmation";

const MAX_PRICE_STEPS = 99;

const parsePriceStep = (raw: string): number | null => {
  const normalized = raw.trim().replace(",", ".");
  if (
    normalized === "" ||
    normalized === "-" ||
    normalized === "." ||
    normalized === "-."
  )
    return null;
  const fractional = normalized.split(".")[1];
  if (fractional && fractional.length > 2) return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
};

const arraysEqual = (a: number[], b: number[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const NordPoolSettings = () => {
  const dispatch = useAppDispatch();
  const savedPriceSteps = useAppSelector(selectNordPoolPriceSteps);

  const [priceSteps, setPriceSteps] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [forcing, setForcing] = useState(false);
  const [forceConfirmOpen, setForceConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    dispatch(fetchNordPoolPriceSteps());
  }, [dispatch]);

  useEffect(() => {
    setPriceSteps(savedPriceSteps);
  }, [savedPriceSteps]);

  const isDirty = !arraysEqual(priceSteps, savedPriceSteps);

  const handleChange = (_: unknown, newValue: (number | string)[]) => {
    const next: number[] = [];
    for (const item of newValue) {
      const value = typeof item === "number" ? item : parsePriceStep(item);
      if (value === null) continue;
      if (next.includes(value)) continue;
      next.push(value);
    }
    setPriceSteps(next.sort((a, b) => a - b).slice(0, MAX_PRICE_STEPS));
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData("text");
    if (!text.includes(";")) return;
    event.preventDefault();
    const parsed = text
      .split(";")
      .map((part) => parsePriceStep(part))
      .filter((value): value is number => value !== null);
    if (parsed.length === 0) return;
    const merged = [...priceSteps];
    for (const value of parsed) {
      if (!merged.includes(value)) merged.push(value);
    }
    setPriceSteps(merged.sort((a, b) => a - b).slice(0, MAX_PRICE_STEPS));
  };

  const handleSave = async () => {
    setSaving(true);
    const ok = await dispatch(saveNordPoolPriceSteps(priceSteps));
    setSaving(false);
    setSnackbar(
      ok
        ? { type: "success", message: "Price steps saved" }
        : { type: "error", message: "Failed to save price steps" },
    );
  };

  const handleForceUpdate = async () => {
    setForceConfirmOpen(false);
    setForcing(true);
    try {
      await forceUpdateNordPoolPriceSteps();
      setSnackbar({
        type: "success",
        message: "All invalid scheduled supply plans removed",
      });
    } catch {
      setSnackbar({
        type: "error",
        message: "Failed to remove invalid scheduled supply plans",
      });
    } finally {
      setForcing(false);
    }
  };

  const handleSnackbarClose = (
    _?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") return;
    setSnackbar(null);
  };

  return (
    <MainLayout tabs={NORD_POOL_TABS}>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5">Settings</Typography>
      </Box>

      <Box sx={{ mt: 4, maxWidth: 700 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
          Price steps (€)
        </Typography>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={priceSteps}
          onChange={handleChange}
          getOptionLabel={(option) =>
            typeof option === "number" ? option.toString() : option
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option.toString()}
                  size="small"
                  {...tagProps}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={
                priceSteps.length >= MAX_PRICE_STEPS
                  ? `Maximum ${MAX_PRICE_STEPS} steps reached`
                  : "Type a number and press Enter, or paste values separated by ;"
              }
              inputProps={{ ...params.inputProps, inputMode: "decimal" }}
              onPaste={handlePaste}
              sx={{ background: "white" }}
            />
          )}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block" }}
        >
          {priceSteps.length} / {MAX_PRICE_STEPS}
        </Typography>
      </Box>

      {isDirty && (
        <Box sx={{ mt: 3 }}>
          <LoadingButton
            variant="contained"
            onClick={handleSave}
            loading={saving}
          >
            Save
          </LoadingButton>
        </Box>
      )}

      <Divider sx={{ my: 4, maxWidth: 700 }} />

      <Box sx={{ maxWidth: 700 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
          Remove invalid scheduled supply plans
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 2, display: "block" }}
        >
          Remove any scheduled supply plans that have become invalid as a result
          of updating price steps.
        </Typography>
        <LoadingButton
          variant="outlined"
          color="warning"
          size="small"
          onClick={() => setForceConfirmOpen(true)}
          loading={forcing}
        >
          Run update
        </LoadingButton>
      </Box>

      <Modal open={forceConfirmOpen} onClose={() => setForceConfirmOpen(false)}>
        <ConfirmationModal
          title="Run price limits update?"
          subtitle="All scheduled supply plans that become invalid will be removed. This action cannot be undone."
          buttonTitle="Run update"
          action={handleForceUpdate}
          setConfirmationModal={setForceConfirmOpen}
        />
      </Modal>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar?.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default NordPoolSettings;
